import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Select,
  MenuItem,
  useMediaQuery,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import TranslateIcon from "@mui/icons-material/Translate";
import logoSvg from "../assets/logo.svg";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

const sections = [
  { label: "services", id: "services" },
  { label: "bookService", id: "booking-section" },
  { label: "about", id: "about" },
  { label: "mission-vision", id: "mission-vision" },
  { label: "crew", id: "crew" },
  { label: "join", id: "join" },
  { label: "contact", id: "contact" },
];

const customMenuProps = {
  MenuProps: {
    PaperProps: {
      sx: {
        background: "rgba(20, 20, 20, 0.95)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: "1px solid rgba(255, 174, 0, 0.25)",
        borderRadius: "16px",
        marginTop: "8px",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.85), 0 0 20px rgba(255, 174, 0, 0.1)",
        "& .MuiMenuItem-root": {
          color: "white",
          fontFamily: "Poppins, Ozen",
          fontSize: "0.88rem",
          py: 1.2,
          px: 2.5,
          justifyContent: "center",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(255, 174, 0, 0.12)",
            color: "#FFAE00",
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(255, 174, 0, 0.18)",
            color: "#FFAE00",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "rgba(255, 174, 0, 0.25)",
            }
          }
        }
      }
    }
  }
};


const Navbar = () => {
  const mobile = useMediaQuery("(max-width: 1024px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const isRTL = i18n.language === "ar";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: any) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setDrawerOpen(false); // Close drawer after click
    }
  };

  const handleLanguageChange = (e: any) => {
    const newLang = e.target.value;
    setLanguage(newLang);
    localStorage.setItem("language", newLang);
    i18n.changeLanguage(newLang);
    window.location.reload();
  };

  return (
    <motion.div
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{
        position: "fixed",
        top: 20,
        left: 0,
        right: 0,
        zIndex: 1000,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: isScrolled ? "86%" : "92%",
          maxWidth: 1200,
          background: isScrolled ? "rgba(235, 160, 0, 0.95)" : "rgba(255, 174, 0, 0.75)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: isScrolled ? "1px solid rgba(255, 255, 255, 0.4)" : "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: isScrolled
            ? "0 12px 40px rgba(0, 0, 0, 0.5), 0 0 25px rgba(255, 174, 0, 0.25)"
            : "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 15px rgba(255, 174, 0, 0.15)",
          borderRadius: "24px",
          px: { xs: 2.5, md: 4 },
          py: 1.5,
          display: "flex",
          flexDirection: isRTL ? "row" : "row-reverse",
          justifyContent: "space-between",
          alignItems: "center",
          color: "white",
          height: isScrolled ? 58 : 65,
          transition: "all 0.4s cubic-bezier(0.25, 1, 0.5, 1)",
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            cursor: "pointer",
          }}
          onClick={() => scrollToSection("home")}
        >
          <motion.img
            whileHover={{ scale: 1.04 }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
            src={logoSvg}
            alt="TowHelp Logo"
            style={{ 
              height: mobile ? 80 : 135,
              filter: "drop-shadow(0 0 5px rgba(255, 255, 255, 0.25))",
              transition: "height 0.4s cubic-bezier(0.25, 1, 0.5, 1)"
            }}
          />
        </Box>

        {/* Desktop Navigation Links */}
        {!mobile && (
          <Box
            sx={{
              display: "flex",
              flexDirection: isRTL ? "row-reverse" : "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            {sections.map((section) => (
              <Button
                key={section.id}
                onMouseEnter={() => setHoveredId(section.id)}
                onMouseLeave={() => setHoveredId(null)}
                sx={{
                  color: "white",
                  textTransform: "none",
                  fontWeight: 600,
                  fontFamily: "Poppins",
                  fontSize: { xs: "0.7rem", md: "0.78rem", lg: "0.88rem" },
                  px: { xs: 1, md: 1.5, lg: 2.2 },
                  py: 1,
                  borderRadius: "12px",
                  position: "relative",
                  zIndex: 2,
                  whiteSpace: "nowrap",
                  transition: "all 0.3s cubic-bezier(0.25, 1, 0.5, 1)",
                  "&:hover": {
                    color: "#ffffff",
                    backgroundColor: "transparent",
                    transform: "translateY(-1px)",
                  },
                }}
                onClick={() => scrollToSection(section.id)}
              >
                {t(section.label)}
                {/* Sliding indicator background capsule and bottom underline */}
                {hoveredId === section.id && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: -1,
                    }}
                  >
                    {/* Background Capsule */}
                    <motion.div
                      layoutId="navbar-hover-pill"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(255, 255, 255, 0.18)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        borderRadius: "10px",
                      }}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                    {/* Underline Indicator */}
                    <motion.div
                      layoutId="navbar-hover-line"
                      style={{
                        position: "absolute",
                        bottom: 2,
                        left: "20%",
                        right: "20%",
                        height: "2px",
                        background: "linear-gradient(90deg, transparent, #ffffff, transparent)",
                        borderRadius: "2px",
                      }}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  </Box>
                )}
              </Button>
            ))}
          </Box>
        )}

        {/* Language Selector / Mobile Menu Trigger */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Desktop Language Selector */}
          {!mobile && (
            <Select
              value={language}
              onChange={handleLanguageChange}
              variant="outlined"
              size="small"
              IconComponent={() => (
                <TranslateIcon 
                  sx={{ 
                    color: "#ffffff", 
                    marginRight: isRTL ? "0px" : "8px", 
                    marginLeft: isRTL ? "8px" : "0px", 
                    fontSize: "1.1rem", 
                    pointerEvents: "none" 
                  }} 
                />
              )}
              {...customMenuProps}
              sx={{
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(6px)",
                color: "#fff",
                fontFamily: "Poppins",
                borderRadius: "30px",
                border: "1px solid rgba(255, 255, 255, 0.25)",
                pl: isRTL ? 0.5 : 1,
                pr: isRTL ? 1 : 0.5,
                height: 38,
                transition: "all 0.3s ease",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent",
                },
                "& .MuiSelect-select": {
                  py: 0.8,
                  pr: isRTL ? "8px !important" : "32px !important",
                  pl: isRTL ? "32px !important" : "8px !important",
                  fontWeight: 500,
                  fontSize: "0.85rem",
                },
                "& .MuiSvgIcon-root": {
                  color: "#ffffff",
                },
                "&:hover": {
                  border: "1px solid #ffffff",
                  backgroundColor: "rgba(255, 255, 255, 0.25)",
                  boxShadow: "0 0 10px rgba(255, 255, 255, 0.2)",
                }
              }}
            >
              <MenuItem value="en">{t('english')}</MenuItem>
              <MenuItem value="ar">{t('العربية')}</MenuItem>
            </Select>
          )}

          {/* Mobile Menu Trigger & Drawer */}
          {mobile && (
            <>
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ 
                  color: "white",
                  border: "1px solid rgba(255, 255, 255, 0.25)",
                  borderRadius: "12px",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  "&:hover": {
                    borderColor: "#ffffff",
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                  }
                }}
              >
                <MenuIcon />
              </IconButton>

              <Drawer
                anchor={isRTL ? "right" : "left"}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                  sx: {
                    backgroundColor: "rgba(18, 18, 18, 0.96)",
                    backdropFilter: "blur(16px)",
                    color: "white",
                    width: "80vw",
                    maxWidth: 300,
                    borderRight: isRTL ? "none" : "1px solid rgba(255, 174, 0, 0.25)",
                    borderLeft: isRTL ? "1px solid rgba(255, 174, 0, 0.25)" : "none",
                  },
                }}
              >
                <Box sx={{ p: 3 }}>
                  <Box height={"90px"} display="flex" justifyContent="center" alignItems="center">
                    <img
                      src={logoSvg}
                      alt="TowHelp Logo"
                      style={{ height: 110, cursor: "pointer" }}
                      onClick={() => scrollToSection("home")}
                    />
                  </Box>
                  <Divider sx={{ my: 2, borderColor: "rgba(255, 174, 0, 0.25)" }} />

                  <List>
                    {sections.map((section) => (
                      <ListItem
                        component="div"
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: "rgba(255, 174, 0, 0.12)",
                            color: "#FFAE00",
                          },
                          backgroundColor: "transparent",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          justifyContent: "center",
                        }}
                      >
                        <ListItemText
                          primary={t(section.label)}
                          primaryTypographyProps={{
                            fontFamily: "Poppins",
                            fontSize: "1rem",
                            fontWeight: 500,
                            textAlign: "center",
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Divider sx={{ my: 2, borderColor: "rgba(255, 255, 255, 0.08)" }} />

                  <Box sx={{ px: 2, mt: 2 }}>
                    <Typography
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        color: "#FFAE00",
                      }}
                    >
                      <TranslateIcon sx={{ fontSize: "1.2rem" }} />
                      {t('language')}
                    </Typography>
                    <Select
                      value={language}
                      onChange={handleLanguageChange}
                      fullWidth
                      variant="outlined"
                      size="small"
                      {...customMenuProps}
                      sx={{
                        background: "#1a1a1a",
                        color: "#fff",
                        fontFamily: "Poppins",
                        borderRadius: 2,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#333",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#FFAE00",
                        },
                        "& .MuiSelect-select": {
                          textAlign: "center",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          pr: "32px !important",
                        }
                      }}
                    >
                      <MenuItem value="en">{t('english')}</MenuItem>
                      <MenuItem value="ar">{t('arabic')}</MenuItem>
                      <MenuItem value="es">{t('spanish')}</MenuItem>
                    </Select>
                  </Box>
                </Box>
              </Drawer>
            </>
          )}
        </Box>
      </Box>
    </motion.div>
  );
};

export default Navbar;


